// Recognizes browser errors emitted when a stale application shell tries to
// load a dynamic-import chunk whose filename has changed after a deployment,
// and reloads the tab once so the fresh shell can load. Another reload is
// allowed only after the guard window, so a later deploy in the same tab
// still recovers while a chunk that stays missing cannot loop.
//
// Non-matching errors are left untouched so Angular/Sentry still see them.

const STALE_IMPORT_PATTERNS: readonly RegExp[] = [
  // Chromium: TypeError: Failed to fetch dynamically imported module: <url>
  /Failed to fetch dynamically imported module/i,
  // Firefox: error loading dynamically imported module
  /error loading dynamically imported module/i,
  // Safari: Importing a module script failed
  /Importing a module script failed/i,
  // Webpack/esbuild chunk loader: ChunkLoadError
  /\bChunkLoadError\b/,
  // Webpack chunk loader: Loading chunk <n> failed
  /Loading chunk\s+\S+\s+failed/i,
];

const SESSION_GUARD_KEY = 'solo:stale-dynamic-import:reload-guard';
// The guard holds the reload time in epoch milliseconds.
const SESSION_GUARD_WINDOW_MS = 5 * 60 * 1000;
const RELOADING_MESSAGE = 'A new version of Solo is ready. Reloading…';
const REFRESH_MESSAGE = 'Solo could not load the latest files. Refresh this page.';
// Shared with the inline script in index.html so every handler in this document sees one reload.
const RELOAD_PENDING_MARKER = '__soloStaleImportReloadPending';

export interface StaleDynamicImportReloadScope {
  __soloStaleImportReloadPending?: boolean;
}

export type StaleDynamicImportRecoveryResult = 'reloaded' | 'shown' | 'ignored';

export interface RecoverStaleDynamicImportOptions {
  platform?: string;
  storage?: Pick<Storage, 'getItem' | 'setItem'>;
  reload?: () => void;
  notify?: (message: string) => void;
  scope?: StaleDynamicImportReloadScope;
  now?: () => number;
}

export function isStaleDynamicImportError(candidate: unknown): boolean {
  const text = extractErrorText(candidate);
  if (!text) {
    return false;
  }
  return STALE_IMPORT_PATTERNS.some(pattern => pattern.test(text));
}

function extractErrorText(candidate: unknown, depth = 0): string {
  if (candidate == null || depth > 3) {
    return '';
  }

  if (typeof candidate === 'string') {
    return candidate;
  }

  if (candidate instanceof Error || candidate instanceof DOMException) {
    return `${candidate.name}: ${candidate.message}`;
  }

  if (typeof ErrorEvent !== 'undefined' && candidate instanceof ErrorEvent) {
    const nested = extractErrorText(candidate.error, depth + 1);
    return nested || candidate.message || '';
  }

  if (typeof PromiseRejectionEvent !== 'undefined' && candidate instanceof PromiseRejectionEvent) {
    return extractErrorText(candidate.reason, depth + 1);
  }

  if (typeof candidate === 'object') {
    const record = candidate as { message?: unknown; name?: unknown; reason?: unknown; error?: unknown };
    if (record.reason !== undefined) {
      const nested = extractErrorText(record.reason, depth + 1);
      if (nested) {
        return nested;
      }
    }
    if (record.error !== undefined) {
      const nested = extractErrorText(record.error, depth + 1);
      if (nested) {
        return nested;
      }
    }
    const name = typeof record.name === 'string' ? record.name : '';
    const message = typeof record.message === 'string' ? record.message : '';
    if (name || message) {
      return name && message ? `${name}: ${message}` : (name || message);
    }
  }

  return '';
}

export interface StaleDynamicImportRecoveryOptions extends RecoverStaleDynamicImportOptions {
  eventTarget?: Pick<EventTarget, 'addEventListener' | 'removeEventListener'>;
}

export type StaleDynamicImportRecoveryUninstall = () => void;

export function installStaleDynamicImportRecovery(
  options: StaleDynamicImportRecoveryOptions = {},
): StaleDynamicImportRecoveryUninstall {
  const target = options.eventTarget ?? (typeof window !== 'undefined' ? window : undefined);
  if (!target) {
    return () => { /* nothing to uninstall */ };
  }

  const recoverOptions: RecoverStaleDynamicImportOptions = {
    platform: options.platform,
    storage: options.storage ?? resolveDefaultStorage(),
    reload: options.reload ?? defaultReload,
    notify: options.notify,
    scope: options.scope,
  };

  const handleErrorEvent = (event: Event): void => {
    recoverStaleDynamicImport(event, recoverOptions);
  };

  const handleRejectionEvent = (event: Event): void => {
    recoverStaleDynamicImport(event, recoverOptions);
  };

  target.addEventListener('error', handleErrorEvent, true);
  target.addEventListener('unhandledrejection', handleRejectionEvent);

  return () => {
    target.removeEventListener('error', handleErrorEvent, true);
    target.removeEventListener('unhandledrejection', handleRejectionEvent);
  };
}

export function recoverStaleDynamicImport(
  candidate: unknown,
  options: RecoverStaleDynamicImportOptions = {},
): StaleDynamicImportRecoveryResult {
  if (!isStaleDynamicImportError(candidate)) {
    return 'ignored';
  }

  // A reload is already under way in this document; later handlers must not overwrite its message.
  const scope = options.scope ?? (globalThis as StaleDynamicImportReloadScope);
  if (scope[RELOAD_PENDING_MARKER] === true) {
    return 'reloaded';
  }

  const platform = options.platform ?? defaultPlatform();
  const notify = options.notify ?? defaultNotify;
  const reload = options.reload ?? defaultReload;
  const native = platform === 'ios' || platform === 'android';
  if (native) {
    notify(REFRESH_MESSAGE);
    return 'shown';
  }

  const storage = options.storage ?? resolveDefaultStorage();
  if (!storage) {
    notify(REFRESH_MESSAGE);
    return 'shown';
  }

  // Fail closed: any storage failure shows the refresh message and does not reload.
  let existing: string | null;
  try {
    existing = storage.getItem(SESSION_GUARD_KEY);
  } catch {
    notify(REFRESH_MESSAGE);
    return 'shown';
  }
  const now = (options.now ?? Date.now)();
  if (isRecentReload(existing, now)) {
    notify(REFRESH_MESSAGE);
    return 'shown';
  }

  try {
    storage.setItem(SESSION_GUARD_KEY, String(now));
  } catch {
    notify(REFRESH_MESSAGE);
    return 'shown';
  }

  notify(RELOADING_MESSAGE);
  scope[RELOAD_PENDING_MARKER] = true;
  reload();
  return 'reloaded';
}

// A missing, unreadable or expired guard (including the old '1' value) allows a reload.
function isRecentReload(existing: string | null, now: number): boolean {
  if (existing === null) {
    return false;
  }
  const reloadedAt = Number(existing);
  if (!Number.isFinite(reloadedAt)) {
    return false;
  }
  const age = now - reloadedAt;
  return age >= 0 && age < SESSION_GUARD_WINDOW_MS;
}

function defaultPlatform(): string {
  try {
    const scope = globalThis as { Capacitor?: { getPlatform?: () => string } };
    const platform = scope.Capacitor?.getPlatform?.();
    if (typeof platform === 'string' && platform.length > 0) {
      return platform;
    }
  } catch {
    return 'web';
  }
  return 'web';
}

function defaultNotify(message: string): void {
  if (typeof document === 'undefined') {
    return;
  }
  const target = document.body ?? document.documentElement;
  if (target) {
    target.textContent = message;
  }
}

function resolveDefaultStorage(): Pick<Storage, 'getItem' | 'setItem'> | undefined {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return undefined;
    }
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

function defaultReload(): void {
  if (typeof window !== 'undefined' && window.location) {
    window.location.reload();
  }
}
